import type { Suggestion } from 'owa-userdataservice';
import { SonoraContact, SonoraContactSuggestion, SonoraEntityType } from 'owa-editor-sonora-types';
import { trace } from 'owa-trace';
import PostalAddressAttributedValue from 'shared/internal/owa-service/src/contract/PostalAddressAttributedValue';
import PersonaPostalAddress from 'shared/internal/owa-service/src/contract/PersonaPostalAddress';
import EmailAddressWrapper from 'shared/internal/owa-service/src/contract/EmailAddressWrapper';
import PhoneNumberType from 'shared/internal/owa-service/src/contract/PhoneNumberType';

export default function parseContactSuggestions(
    suggestion: Suggestion
): SonoraContactSuggestion | undefined {
    try {
        const data = JSON.parse(suggestion.encodedJsonData);

        if (data) {
            let contactSuggestions = data.reduce((contactSuggestionsResult, contactInfo) => {
                // Only display contact suggestions if they have more information than just one email
                if (
                    contactInfo.EmailAddresses.length > 1 ||
                    contactInfo.PhoneNumbers.length > 0 ||
                    contactInfo.Addresses.length > 0
                ) {
                    contactSuggestionsResult.push(<SonoraContact>{
                        type: SonoraEntityType.Contact,
                        id: contactInfo.Id,
                        displayName: contactInfo.DisplayName,
                        jobTitle: contactInfo.JobTitle,
                        peopleSubtype: contactInfo.PeopleSubtype,
                        emailAddresses: convertEmails(contactInfo.EmailAddresses),
                        phoneNumbers: convertPhones(contactInfo.PhoneNumbers),
                        addresses: convertAddresses(contactInfo.Addresses),
                    });
                }
                return contactSuggestionsResult;
            }, []);

            return {
                suggestions: contactSuggestions,
                logicalId: suggestion.logicalId,
                traceId: suggestion.traceId,
            };
        }
    } catch (error) {
        trace.warn(`[HoneybeeSonora|parseContactSuggestion] Error: ${error}`);
    }

    return undefined;
}

function convertPhones(phones: any): PhoneNumberType[] | undefined {
    if (phones != null) {
        let phoneWrapper = phones.map(
            phone =>
                <PhoneNumberType>{
                    Number: phone?.PhoneString,
                    Type: phone?.Type,
                }
        );
        return phoneWrapper;
    }

    return undefined;
}

function convertEmails(emails: any): EmailAddressWrapper[] | undefined {
    if (emails != null) {
        let emailWrapper = emails.map(
            email =>
                <EmailAddressWrapper>{
                    Name: email?.EmailAddress,
                    MailboxType: email?.MailboxType,
                }
        );
        return emailWrapper;
    }

    return undefined;
}

function convertAddresses(addresses: any): PostalAddressAttributedValue[] | undefined {
    if (addresses != null) {
        let postalAddresses = addresses.map(
            address =>
                <PostalAddressAttributedValue>{
                    Value: <PersonaPostalAddress>{
                        Type: address?.Type,
                        Street: address?.Street,
                        City: address?.City,
                        State: address?.State,
                        Country: address?.Country,
                        PostalCode: address?.PostalCode,
                    },
                }
        );
        return postalAddresses;
    }

    return undefined;
}
